import React, { createContext, useContext, useState, useCallback } from 'react';
import { 
  Snackbar, 
  Alert, 
  AlertTitle,
  Box,
  IconButton,
  Typography
} from '@mui/material';
import { 
  CheckCircle, 
  Error, 
  Warning, 
  Info, 
  Close
} from '@mui/icons-material';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const addToast = useCallback(({ 
    message, 
    type = 'info', 
    title, 
    duration = 5000, 
    action,
    persistent = false 
  }) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, title, duration, action, persistent };
    
    setToasts(prev => [...prev, newToast]);
    
    if (!persistent && duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
    
    return id;
  }, [removeToast]);

  const success = useCallback((message, options = {}) => {
    return addToast({ message, type: 'success', ...options });
  }, [addToast]);

  const error = useCallback((message, options = {}) => {
    return addToast({ message, type: 'error', ...options });
  }, [addToast]);

  const warning = useCallback((message, options = {}) => {
    return addToast({ message, type: 'warning', ...options });
  }, [addToast]);

  const info = useCallback((message, options = {}) => {
    return addToast({ message, type: 'info', ...options });
  }, [addToast]);

  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle sx={{ color: 'var(--success-color)' }} />;
      case 'error':
        return <Error sx={{ color: 'var(--error-color)' }} />;
      case 'warning':
        return <Warning sx={{ color: 'var(--warning-color)' }} />;
      case 'info':
        return <Info sx={{ color: 'var(--primary-color)' }} />;
      default:
        return <Info sx={{ color: 'var(--primary-color)' }} />;
    }
  };

  const getBackgroundColor = (type) => {
    switch (type) {
      case 'success':
        return 'rgba(16, 245, 160, 0.1)';
      case 'error':
        return 'rgba(239, 68, 68, 0.1)';
      case 'warning':
        return 'rgba(251, 191, 36, 0.1)';
      case 'info':
        return 'rgba(0, 212, 255, 0.1)';
      default:
        return 'rgba(0, 212, 255, 0.1)';
    }
  };

  const getBorderColor = (type) => {
    switch (type) {
      case 'success':
        return 'var(--success-color)';
      case 'error':
        return 'var(--error-color)';
      case 'warning':
        return 'var(--warning-color)';
      case 'info':
        return 'var(--primary-color)';
      default:
        return 'var(--primary-color)';
    }
  };

  return (
    <ToastContext.Provider value={{ addToast, removeToast, success, error, warning, info }}>
      {children}
      
      {/* Toast Container */}
      <Box
        sx={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxWidth: 400
        }}
      >
        {toasts.map((toast) => (
          <Box
            key={toast.id}
            sx={{
              backgroundColor: 'var(--background-card)',
              border: `1px solid ${getBorderColor(toast.type)}`,
              borderRadius: '8px',
              boxShadow: 'var(--shadow-lg)',
              p: 2,
              minWidth: 300,
              animation: 'slideInRight 0.3s ease-out',
              '@keyframes slideInRight': {
                '0%': {
                  transform: 'translateX(100%)',
                  opacity: 0
                },
                '100%': {
                  transform: 'translateX(0)',
                  opacity: 1
                }
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ mt: 0.5 }}>
                {getIcon(toast.type)}
              </Box>
              
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {toast.title && (
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'var(--text-primary)',
                      mb: 0.5
                    }}
                  >
                    {toast.title}
                  </Typography>
                )}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'var(--text-secondary)',
                    wordBreak: 'break-word'
                  }}
                >
                  {toast.message}
                </Typography>
                
                {toast.action && (
                  <Box sx={{ mt: 1 }}>
                    {toast.action}
                  </Box>
                )}
              </Box>
              
              <IconButton
                size="small"
                onClick={() => removeToast(toast.id)}
                sx={{ 
                  color: 'var(--text-muted)',
                  '&:hover': {
                    color: 'var(--text-secondary)'
                  }
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
